import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Printer, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

export function DesktopMethodSection() {
  const { authAxios } = useAuth();

  const handleCheckQueue = async () => {
    try {
      const r = await authAxios.get("/print/history?limit=10");
      const data = r.data?.data || r.data;
      const jobs = data?.jobs || [];
      if (jobs.length === 0) {
        toast.info("Nenhum trabalho de impressao recente");
      } else {
        const pending = jobs.filter(j => j.status === "pending").length;
        const done = jobs.filter(j => j.status === "completed").length;
        toast.success(`${pending} pendente(s), ${done} impresso(s)`);
      }
    } catch (e) {
      toast.error("Erro ao verificar fila: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border-l-4 border-l-orange-500" data-testid="kaiso-print-section">
        <CardContent className="py-6">
          <div className="flex items-start gap-3">
            <Printer className="w-8 h-8 text-orange-500 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-bold text-lg text-gray-900">Kaiso Print Station</h3>
              <p className="text-sm text-gray-600 mt-1">
                Impressao automatica direto no navegador. Sem instalar nada!
                Abra a pagina, deixe a aba aberta e os recibos imprimem automaticamente quando chega um pedido.
              </p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2 text-green-700"><CheckCircle2 className="w-4 h-4" /> Zero instalacao</div>
                <div className="flex items-center gap-2 text-green-700"><CheckCircle2 className="w-4 h-4" /> Funciona com qualquer impressora</div>
                <div className="flex items-center gap-2 text-green-700"><CheckCircle2 className="w-4 h-4" /> Impressao automatica de pedidos</div>
                <div className="flex items-center gap-2 text-green-700"><CheckCircle2 className="w-4 h-4" /> Qualquer computador com navegador</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Como usar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <p className="text-sm text-gray-600"><strong>Opcao 1 - Automatico (Recomendado):</strong></p>
            <div className="bg-gray-50 border rounded-lg p-3 space-y-2">
              <p className="text-sm text-gray-700"><strong>1.</strong> Configure a impressora termica como <strong>impressora padrao</strong> do Windows</p>
              <p className="text-sm text-gray-700"><strong>2.</strong> Baixe o atalho automatico:</p>
              <Button
                variant="outline"
                className="w-full h-12 border-2 border-orange-300 hover:bg-orange-50 gap-2"
                onClick={() => {
                  const a = document.createElement("a");
                  a.href = `${API}/api/print/kiosk-shortcut`;
                  a.download = "Kaiso_Print_Automatico.bat";
                  a.click();
                  toast.success("Atalho baixado! Clique 2x para abrir.");
                }}
                data-testid="download-kiosk-shortcut-btn"
              >
                <Download className="w-5 h-5" />
                Baixar Atalho Automatico (Windows)
              </Button>
              <p className="text-xs text-gray-500">Clique 2x no arquivo baixado. O Chrome abre e imprime automaticamente sem perguntar!</p>
            </div>

            <p className="text-sm text-gray-600 mt-4"><strong>Opcao 2 - Manual (com estacoes):</strong></p>
            <p className="text-sm text-gray-500 mb-2">Abra uma aba para cada ponto de impressao:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button className="h-12 bg-orange-600 hover:bg-orange-700 text-white gap-2" onClick={() => window.open("/print-station", "_blank")} data-testid="open-print-station-btn">
                <Printer className="w-4 h-4" /> Todas as estacoes
              </Button>
              <Button variant="outline" className="h-12 gap-2 border-amber-300 hover:bg-amber-50" onClick={() => window.open("/print-station?station=caixa", "_blank")} data-testid="open-station-caixa-btn">Caixa</Button>
              <Button variant="outline" className="h-12 gap-2 border-red-300 hover:bg-red-50" onClick={() => window.open("/print-station?station=cozinha", "_blank")} data-testid="open-station-cozinha-btn">Cozinha</Button>
              <Button variant="outline" className="h-12 gap-2 border-purple-300 hover:bg-purple-50" onClick={() => window.open("/print-station?station=bar", "_blank")} data-testid="open-station-bar-btn">Bar</Button>
            </div>
            <p className="text-sm text-gray-500 mt-2">Cada aba recebe apenas os itens da sua estacao. Na primeira vez, selecione a impressora termica.</p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Dica importante:</strong> No dialogo de impressao, mude o "Destino" de "Guardar como PDF" para a sua <strong>impressora termica</strong> (ex: POS-80, Epson TM-T20, etc).
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fila de Impressao</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={handleCheckQueue} data-testid="check-queue-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Verificar Fila
            </Button>
            <span className="text-xs text-gray-500">Veja o status dos trabalhos de impressao</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
